// packages/web-ui/src/AppShell/UtilityDrawer.tsx
//
// W2-S2 ACs #1/#2/#3 + W2-S9 ACs #1/#2/#3 — UtilityDrawer.
//
// Composition:
//   <aside role="complementary"> (always-mounted; width=0 when closed so the
//                                 AppShell flex-row collapses but the screen
//                                 reader skips an empty region — the inner
//                                 body is suppressed when closed)
//     <DrawerTabStrip />                 ← W2-S9 (this story)
//     <UtilityDrawerContent />           ← W2-S9 (this story)
//   </aside>
//
// W2-S2 invariants preserved:
//   - The component is rendered ALWAYS (AppShell.test.tsx relies on
//     `getByTestId('utility-drawer')` even when `open === false`). Width
//     collapses to 0 px when closed.
//   - `data-mode` and `data-open` attributes are exposed for AppShell tests.
//
// W2-S9 additions:
//   - Tab strip (Activity / Detail / Debug) with conditional Debug visibility.
//   - `Ctrl+J` toggles the drawer (registered globally — works even when the
//     drawer body is suppressed).
//   - `Ctrl+Shift+J` opens the drawer to Debug + flips the session-only
//     `debugOverride` flag (UX §7.6).
//   - `Esc` (when focus is inside the drawer) closes the drawer.
//   - Auto-tab on destination change — the drawer's active tab is recomputed
//     when `navigation.activeDestination` changes (UX §7.3).
//   - Dynamic `aria-label` on the `<aside>` reflects the active drawer tab.
//
// Power-user toggle settings flag is persisted to localStorage as a Tier-2
// fallback until W6-S1 wires `GET /api/v1/settings`.

import {
  useCallback,
  useEffect,
  useRef,
  useSyncExternalStore,
  type CSSProperties,
  type JSX,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { Z_INDEX, type Breakpoint } from './responsive-tokens.js';
import { useReducedMotion } from './useReducedMotion.js';
import { useRootStore } from '../state/store.js';
import {
  createUtilityDrawerStore,
  type DrawerTab,
  type UtilityDrawerSlice,
} from '../state/utilityDrawer.js';
import { DrawerTabStrip } from './drawer-tabs/DrawerTabStrip.js';
import { UtilityDrawerContent } from './drawer-tabs/UtilityDrawerContent.js';
import { getDefaultDrawerTab } from './drawer-tabs/get-default-drawer-tab.js';
import {
  CYCLE_DRAWER_TAB_EVENT,
  TOGGLE_DEBUG_TAB_EVENT,
  type CycleDrawerTabDetail,
} from '../hooks/useKeymap.js';

export type UtilityDrawerMode = 'inline' | 'popover' | 'hidden';

export interface UtilityDrawerProps {
  width: number;
  open: boolean;
  mode: UtilityDrawerMode;
  breakpoint: Breakpoint;
}

const TAB_LABELS: Record<DrawerTab, string> = {
  activity: 'Activity',
  detail: 'Detail',
  debug: 'Debug',
};

/**
 * Module-level singleton store for drawer-tab state. Kept outside the
 * `useRootStore` composition for parallel-lane safety — the W2-S9
 * `state/store.ts` modification belongs to a sibling lane and risks
 * cross-agent interference. When the root store composes this slice,
 * the singleton becomes the canonical source-of-truth and tests can
 * `_reset()` between cases.
 */
const drawerStore = createUtilityDrawerStore();

/** Test-only — reset the drawer's tab/override state. */
export function _resetUtilityDrawerForTests(): void {
  drawerStore.getState()._reset();
}

/** Subscribes to the drawer store and returns the snapshot. */
function useDrawerSlice<T>(selector: (state: UtilityDrawerSlice) => T): T {
  return useSyncExternalStore(
    drawerStore.subscribe,
    () => selector(drawerStore.getState()),
    () => selector(drawerStore.getState()),
  );
}

const SHOW_DEBUG_TAB_LS_KEY = 'tessa-lean.ui.showDebugTab';
const AUTO_OPEN_ON_ERROR_LS_KEY = 'tessa-lean.ui.utilityDrawer.autoOpenOnError';

/** One-time hydration of the two boolean settings from localStorage. */
let settingsHydrated = false;
function hydrateSettingsOnce(): void {
  if (settingsHydrated) return;
  settingsHydrated = true;
  if (typeof window === 'undefined') return;
  try {
    const showDebug = window.localStorage.getItem(SHOW_DEBUG_TAB_LS_KEY);
    if (showDebug === 'true') drawerStore.getState().setShowDebugTab(true);

    const auto = window.localStorage.getItem(AUTO_OPEN_ON_ERROR_LS_KEY);
    if (auto === 'false') drawerStore.getState().setAutoOpenOnError(false);
  } catch {
    /* localStorage may be disabled — defaults are fine */
  }
}

export function UtilityDrawer({ width, open, mode, breakpoint }: UtilityDrawerProps): JSX.Element {
  // ─── External (root) state ─────────────────────────────────────────────
  const activeDestination = useRootStore((s) => s.activeDestination);
  const utilityDrawerOpen = useRootStore((s) => s.utilityDrawerOpen);
  const toggleUtilityDrawer = useRootStore((s) => s.toggleUtilityDrawer);

  // ─── Drawer-tab slice ──────────────────────────────────────────────────
  hydrateSettingsOnce();
  const activeTab = useDrawerSlice((s) => s.activeTab);
  const debugOverride = useDrawerSlice((s) => s.debugOverride);
  const showDebugTab = useDrawerSlice((s) => s.showDebugTab);
  const lastUsedByDestination = useDrawerSlice((s) => s.lastUsedByDestination);

  const showDebug = showDebugTab || debugOverride;

  // ─── Auto-tab on destination change (UX §7.3) ──────────────────────────
  // Whenever the destination changes, recompute the canonical drawer tab
  // (so opening with Ctrl+J after a destination switch lands on the right
  // tab). Uses a ref to track the last-seen destination so we only fire
  // when the destination actually changes (not on tab clicks within the
  // same destination).
  const lastDestinationRef = useRef(activeDestination);
  useEffect(() => {
    if (lastDestinationRef.current === activeDestination) return;
    lastDestinationRef.current = activeDestination;
    const next = getDefaultDrawerTab(activeDestination, lastUsedByDestination);
    drawerStore.getState().setActiveTab(next);
  }, [activeDestination, lastUsedByDestination]);

  // ─── Keyboard shortcuts (Ctrl+J / Ctrl+Shift+J) ────────────────────────
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handler = (e: KeyboardEvent): void => {
      // Ignore when modifier-only or when target is inside an editable
      // element (text inputs, textarea, contenteditable) — those fields
      // own Ctrl+J for their own purposes (rare, but defensive).
      if (e.repeat) return;
      const target = e.target as HTMLElement | null;
      if (target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA') return;
      if (target?.isContentEditable) return;

      const isCtrlJ = e.ctrlKey && !e.metaKey && !e.altKey && e.key.toLowerCase() === 'j';
      if (!isCtrlJ) return;

      if (e.shiftKey) {
        // Ctrl+Shift+J — power-user override + open to Debug.
        e.preventDefault();
        const isOpen = useRootStore.getState().utilityDrawerOpen;
        drawerStore.getState().setDebugOverride(true);
        if (!isOpen) {
          // Open and switch to Debug.
          useRootStore.getState().toggleUtilityDrawer();
          drawerStore.getState().selectTabForDestination(
            'debug',
            useRootStore.getState().activeDestination,
          );
        } else {
          // Already open — close (matches `Ctrl+J` toggle semantics);
          // override remains sticky for the session.
          useRootStore.getState().toggleUtilityDrawer();
        }
        return;
      }

      // Plain Ctrl+J — toggle.
      e.preventDefault();
      useRootStore.getState().toggleUtilityDrawer();
    };

    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('keydown', handler);
    };
  }, []);

  // ─── Polish §8 — custom-event channels (Ctrl+Shift+J + 1/2/3) ──────────
  // `useGlobalKeymap()` dispatches:
  //   - `tessa:toggleDebugTab`  → mirrors the Ctrl+Shift+J action below
  //                               (open + override + Debug tab); fires from
  //                               menus / palette items that don't go
  //                               through the keydown path.
  //   - `tessa:cycleDrawerTab`  → switches the active drawer tab to the
  //                               `index` carried in `event.detail` (0/1/2
  //                               for Activity/Detail/Debug). Only fires
  //                               from `useGlobalKeymap` when focus is in
  //                               the drawer + not editable, so we don't
  //                               re-guard here.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    function handleToggleDebugTab(): void {
      // Mirror the Ctrl+Shift+J keydown handler above: flip the override on
      // unconditionally + ensure the drawer is open showing the Debug tab.
      const root = useRootStore.getState();
      drawerStore.getState().setDebugOverride(true);
      if (!root.utilityDrawerOpen) {
        root.toggleUtilityDrawer();
      }
      drawerStore
        .getState()
        .selectTabForDestination('debug', root.activeDestination);
    }

    function handleCycleDrawerTab(event: Event): void {
      const ce = event as CustomEvent<CycleDrawerTabDetail>;
      const index = ce.detail?.index;
      if (index === undefined) return;
      // Resolve the three tab keys in the strip's display order.
      // `Debug` is only reachable when visible (showDebug); if the user
      // presses `3` while Debug is hidden, no-op rather than landing on a
      // hidden tab.
      const slice = drawerStore.getState();
      const showDebug = slice.showDebugTab || slice.debugOverride;
      const TABS: ReadonlyArray<DrawerTab | null> = [
        'activity',
        'detail',
        showDebug ? 'debug' : null,
      ];
      const tab = TABS[index];
      if (tab === null || tab === undefined) return;
      slice.selectTabForDestination(tab, useRootStore.getState().activeDestination);
    }

    window.addEventListener(TOGGLE_DEBUG_TAB_EVENT, handleToggleDebugTab);
    window.addEventListener(CYCLE_DRAWER_TAB_EVENT, handleCycleDrawerTab);
    return () => {
      window.removeEventListener(TOGGLE_DEBUG_TAB_EVENT, handleToggleDebugTab);
      window.removeEventListener(CYCLE_DRAWER_TAB_EVENT, handleCycleDrawerTab);
    };
  }, []);

  // ─── Esc inside the drawer closes it ───────────────────────────────────
  const onAsideKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLElement>): void => {
      if (e.key !== 'Escape') return;
      // Only fire when the drawer is actually open — `Esc` should be a
      // no-op on a 0-width closed drawer (defensive; the closed body is
      // not focusable anyway).
      if (!utilityDrawerOpen) return;
      e.stopPropagation();
      toggleUtilityDrawer();
    },
    [utilityDrawerOpen, toggleUtilityDrawer],
  );

  // ─── Tab change handler ────────────────────────────────────────────────
  const onTabChange = useCallback(
    (tab: DrawerTab) => {
      drawerStore.getState().selectTabForDestination(tab, activeDestination);
    },
    [activeDestination],
  );

  // ─── Render ────────────────────────────────────────────────────────────
  const effectiveWidth = open && mode !== 'hidden' ? width : 0;

  // W2-S10b Task 4.4 (AC #5b) — slide animation gated on reduced-motion.
  // The drawer slides open/closed via a 200 ms width transition. When the
  // user prefers reduced motion (or the global `--reduced-motion` flag is
  // set), the transition is dropped and the width changes instantly.
  const reducedMotion = useReducedMotion();
  const transitionStyle: CSSProperties = reducedMotion
    ? {}
    : { transition: 'width 200ms ease-out' };

  const style: CSSProperties =
    mode === 'popover'
      ? { width: effectiveWidth, zIndex: Z_INDEX.popover, ...transitionStyle }
      : { width: effectiveWidth, zIndex: Z_INDEX.baseCanvas, ...transitionStyle };

  // Dynamic aria-label per UX §18 line 2005 — reflects the active drawer tab.
  const ariaLabel = open
    ? `Utility drawer — ${TAB_LABELS[activeTab]}`
    : 'Utility drawer';

  return (
    <aside
      role="complementary"
      aria-label={ariaLabel}
      data-testid="utility-drawer"
      data-mode={mode}
      data-open={open ? 'true' : 'false'}
      data-active-tab={activeTab}
      data-show-debug={showDebug ? 'true' : 'false'}
      data-breakpoint={breakpoint}
      tabIndex={-1}
      onKeyDown={onAsideKeyDown}
      className="bg-cream-100 border-l border-taupe-300 flex flex-col overflow-hidden"
      style={style}
    >
      {open && mode !== 'hidden' ? (
        <>
          <DrawerTabStrip
            activeTab={activeTab}
            onTabChange={onTabChange}
            showDebug={showDebug}
          />
          <div className="flex flex-1 flex-col overflow-hidden">
            <UtilityDrawerContent
              activeTab={activeTab}
              destination={activeDestination}
            />
          </div>
        </>
      ) : null}
    </aside>
  );
}
